import React, { useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Facebook, Twitter, Send, Sparkles, CreditCard, Wallet, Landmark, Smartphone, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const linkGroups = [
  { title: "Collections", links: ["Boardroom Premium", "Shantanu & Nikhil Luxury", "Festive Express Essentials"] },
  { title: "Company", links: ["How It Works", "Why Trust Us", "Powered by Consortium Gifts", "Careers"] },
  { title: "Support", links: ["Track Order", "Shipping Policy", "Returns", "Contact Us"] },
];

const payments = [
  { icon: CreditCard, label: "Cards" },
  { icon: Smartphone, label: "UPI" },
  { icon: Wallet, label: "Wallets" },
  { icon: Landmark, label: "NetBanking" },
];

const Footer = () => {
  const [email, setEmail] = useState("");

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    toast.success("You're on the VIP list — early access & exclusive offers incoming!");
    setEmail("");
  };

  return (
    <footer className="relative z-10 overflow-hidden border-t border-[#F3E5AB]/12 bg-[#0d1220]" data-testid="footer">
      <div className="pointer-events-none absolute -left-40 top-0 h-96 w-96 bloom-indigo opacity-60" />
      <div className="pointer-events-none absolute right-0 top-0 h-80 w-80 bloom-gold opacity-30" />

      {/* Newsletter */}
      <div className="relative mx-auto max-w-7xl border-b border-[#F3E5AB]/10 px-6 py-16 md:px-10">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Unlock VIP Early Access</span>
            <h3 className="mt-4 font-serif text-4xl leading-tight tracking-tight text-[#F3EEE3] md:text-5xl">
              Be first to the finest.<br />
              <span className="text-[#D4AF37]">Exclusive festive drops.</span>
            </h3>
          </div>
          <form onSubmit={handleSubscribe} className="flex w-full flex-col gap-3 sm:flex-row" data-testid="newsletter-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              data-testid="newsletter-email-input"
              className="h-14 w-full flex-1 rounded-full border border-[#F3E5AB]/15 bg-white/5 px-6 font-sans text-sm text-[#F3EEE3] placeholder:text-[#8B93A7] outline-none transition-colors duration-300 focus:border-[#D4AF37]"
            />
            <button
              type="submit"
              data-testid="newsletter-submit-btn"
              className="btn-gold flex h-14 items-center justify-center gap-2 rounded-full px-8 font-sans text-sm font-semibold tracking-wide"
            >
              Join VIP <Send className="h-4 w-4" strokeWidth={2} />
            </button>
          </form>
        </div>
      </div>

      {/* Links */}
      <div className="relative mx-auto grid max-w-7xl grid-cols-2 gap-10 px-6 py-16 md:grid-cols-4 md:px-10">
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="font-serif text-xl tracking-tight text-[#F3EEE3]">
              Diwali Gifts <span className="text-[#D4AF37]">In Two Days</span>
            </span>
          </div>
          <p className="mt-4 max-w-xs font-sans text-sm leading-relaxed text-[#8B93A7]">
            An express festive gifting initiative by Consortium Gifts — premium Diwali
            gifts, curated and guaranteed to arrive within 2 days.
          </p>
          <div className="mt-6 flex gap-3">
            {[Instagram, Facebook, Twitter].map((Icon, i) => (
              <a
                key={i}
                href="#"
                data-testid={`social-link-${i}`}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-[#F3E5AB]/15 text-[#8B93A7] transition-all duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
              >
                <Icon className="h-4 w-4" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </div>

        {linkGroups.map((group) => (
          <div key={group.title}>
            <h4 className="font-sans text-xs uppercase tracking-[0.2em] text-[#D4AF37]">{group.title}</h4>
            <ul className="mt-5 space-y-3">
              {group.links.map((link) => (
                <li key={link}>
                  <a href="#" className="font-sans text-sm text-[#8B93A7] transition-colors duration-300 hover:text-[#F3EEE3]">{link}</a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Consortium external card */}
      <div className="relative mx-auto max-w-7xl px-6 pb-4 md:px-10">
        <a
          href="https://www.consortiumgifts.com"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-consortium-card"
          className="glass group flex flex-col items-start justify-between gap-4 rounded-2xl border border-[#F3E5AB]/15 p-6 transition-colors duration-300 hover:border-[#D4AF37]/50 sm:flex-row sm:items-center md:p-8"
        >
          <p className="max-w-2xl font-sans text-sm leading-relaxed text-[#E8E2D0]">
            Need year-round corporate gifts, custom logo branding, or international delivery?{" "}
            <span className="text-[#D4AF37]">Visit ConsortiumGifts.com</span>
          </p>
          <span className="btn-gold inline-flex shrink-0 items-center gap-2 rounded-full px-6 py-3 font-sans text-sm font-semibold tracking-wide">
            Explore Consortium Gifts
            <ExternalLink className="h-4 w-4" strokeWidth={2.2} />
          </span>
        </a>
      </div>

      {/* Payments + copyright */}
      <div className="relative mx-auto flex max-w-7xl flex-col items-center justify-between gap-5 border-t border-[#F3E5AB]/10 px-6 py-7 md:flex-row md:px-10">
        <p className="font-sans text-xs tracking-wide text-[#8B93A7]">
          diwaligiftsintwodays.com is an express delivery initiative by Consortium Gifts. © {new Date().getFullYear()}
        </p>
        <div className="flex items-center gap-3">
          <span className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#8B93A7]">Secure payments</span>
          <div className="flex gap-2">
            {payments.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.label} title={p.label} className="flex h-8 w-11 items-center justify-center rounded-md border border-[#F3E5AB]/12 bg-white/5 text-[#C9CEDA]">
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
